import { mapEditorModeStore } from "../../Stores/MapEditorStore";
import { Easing } from "../../types";
import { HtmlUtils } from "../../WebRtc/HtmlUtils";
import type { Box } from "../../WebRtc/LayoutManager";
import { ZOOM_DISCRETE_LEVEL_COUNT, ZOOM_MAX_STEPS_PER_EVENT, ZOOM_WHEEL_STEP } from "../../Enum/EnvironmentVariable";
import type { Player } from "../Player/Player";
import { hasMovedEventName } from "../Player/Player";
import type { WaScaleManagerFocusTarget, WaScaleManager } from "../Services/WaScaleManager";
import { waScaleManager, WaScaleManagerEvent } from "../Services/WaScaleManager";
import type { ActiveEventList } from "../UserInput/UserInputManager";
import { UserInputEvent } from "../UserInput/UserInputManager";
import { debugZoom } from "../../Utils/Debuggers";
import type { RemotePlayer } from "../Entity/RemotePlayer";
import { PropertyUtils } from "../Map/PropertyUtils";
import type { GameScene } from "./GameScene";
import Clamp = Phaser.Math.Clamp;

export enum CameraMode {
    /**
     * Camera looks at certain point but is not locked and will start following the player on his movement
     */
    Positioned = "Positioned",
    /**
     * Camera is actively following the player
     */
    Follow = "Follow",
    /**
     * Camera is focusing on certain point and will not break this focus even on player movement
     */
    Focus = "Focus",

    /**
     * Camera is free and can be moved anywhere on the map by the user (only in the exploration mode)
     */
    Exploration = "Exploration",
}

export enum CameraManagerEvent {
    CameraUpdate = "CameraUpdate",
}

export interface CameraManagerEventCameraUpdateData {
    x: number;
    y: number;
    width: number;
    height: number;
    zoom: number;
}

export interface CameraZoomStateSnapshot {
    cameraZoom: number;
    normalizedDiscreteLevel: number;
    effectiveCssZoom?: number; // NEW: camera.zoom * scene.scale.zoom
}

export interface StartFollowPlayerOptions {
    preserveZoomOnComplete?: boolean;
    freezeTargetDuringTransition?: boolean;
    smoothCatchUpMs?: number;
}

type WaCameraExperimentControls = {
    topOverscanEnabled: boolean;
    topOverscanWorld: number;
};

/**
 * The CameraManager is responsible for managing the camera in the game.
 * It allows to set the camera to follow the player, to focus on a specific point or to be in exploration mode.
 *
 * The CameraManager handles the transitions / animations between the different camera modes.
 * It also handles the smooth zoom in and out of the camera.
 */
export class CameraManager extends Phaser.Events.EventEmitter {
    private camera: Phaser.Cameras.Scene2D.Camera;
    private waScaleManager: WaScaleManager;

    private cameraMode: CameraMode = CameraMode.Positioned;

    private restoreZoomTween?: Phaser.Tweens.Tween;
    private startFollowTween?: Phaser.Tweens.Tween;
    private followTransitionInProgress = false;

    private playerToFollow?: Player | RemotePlayer;
    private cameraLocked: boolean;
    private zoomLocked: boolean;

    private readonly EDITOR_MODE_SCROLL_SPEED: number = 5;
    private readonly FOLLOW_MIN_ZOOM_MARGIN = 1.05;
    private readonly SAFE_MIN_ZOOM_OUT_FACTOR = 1.05;
    private readonly DISCRETE_EDGE_SKIP_LEVELS = 1;
    private readonly DISCRETE_ZOOM_LEVEL_COUNT = Math.max(2, ZOOM_DISCRETE_LEVEL_COUNT);
    private readonly NORMALIZED_WHEEL_STEP = Math.max(1, ZOOM_WHEEL_STEP);
    private readonly MAX_WHEEL_STEPS_PER_EVENT = Math.max(1, ZOOM_MAX_STEPS_PER_EVENT);
    private readonly ZOOM_INTEGER_EPSILON = 0.000001;
    private readonly BOUNDARY_INSET_PX = 1;
    private readonly CAMERA_BOUNDARY_SLACK_WORLD = 0.5;
    private readonly CAMERA_SEAM_DEBUG_DEFAULT = false;
    private readonly CAMERA_TOP_OVERSCAN_DEFAULT_WORLD = 96;
    private readonly CAMERA_FIT_EXCLUDE_TOP_PROPERTY = "cameraFitExcludeTop";

    private unsubscribeMapEditorModeStore: () => void;
    private mapEditorModeOpened = false;
    private lastAppliedMapBoundsSignature: string | undefined;
    private lastAppliedFitZoomSignature: string | undefined;

    // Whether a pan or tween effect is in progress
    private animationInProgress = false;
    // Are we yet arrived to targetZoomModifier?
    private targetReachInProgress = false;
    // The target zoom we should reach. Each step, we get closer to this target.
    private targetZoomModifier: number | undefined;
    private targetDirection: "zoom_out" | "zoom_in" | undefined;
    private cameraZoomSpeed = 1;
    private _resistanceStartZoomLevel = 0.6;
    private _resistanceEndZoomLevel = 0.3;
    // The resistance strength is the speed at which the camera will go back to the resistance start zoom level.
    private _resistanceStrength = 1;
    // The callback to be called when the resistance zone is overcome
    private resistanceCallback?: () => void;
    private animateCallback: (time: number, delta: number) => void;
    // The date when the resistance wall was broken
    private wallDownDate = 0;
    private resistanceZoneEnterDate = 0;
    private cameraSpeed: { x: number; y: number } | undefined;
    // If set to false, the resistance wall will never be active
    private enableResistanceWall = false;
    private resistanceRadiusAroundWoka: number | undefined;
    private player: Player | undefined;

    // The point of the scene the explorer mode is focusing on.
    private explorerFocusOn: { x: number; y: number } = { x: 0, y: 0 };
    // If set, the camera will move toward this target.
    private explorerFocusOnTarget: { x: number; y: number; zoom: number } | undefined;
    private zoomAnchor:
        | {
              screenX: number;
              screenY: number;
              worldX: number;
              worldY: number;
          }
        | undefined;
    private focusTargetSpeed = 0.2;
    private explorationAllowOutsideMap = true;
    private wheelZoomAccumulator = 0;
    private resizeInProgress = false;
    private unhookCameraPreRender?: () => void;

    // The tween for the camera offset
    private cameraOffsetCurrentTween?: Phaser.Tweens.Tween;
    private readonly mapFitExcludeTopWorldFromMap: number;

    constructor(
        private scene: GameScene,
        private mapSize: { width: number; height: number },
        waScaleManager: WaScaleManager
    ) {
        super();
        this.animateCallback = this.animate.bind(this);

        this.camera = scene.cameras.main;
        this.mapFitExcludeTopWorldFromMap = this.readMapFitExcludeTopWorldFromMap();
        const seamDebugWindow = window as unknown as {
            __wa_scene?: GameScene;
            __wa_camera?: Phaser.Cameras.Scene2D.Camera;
        };
        seamDebugWindow.__wa_scene = this.scene;
        seamDebugWindow.__wa_camera = this.camera;
        this.ensureCameraExperimentControls();
        if (this.isSeamDebugEnabled()) {
            console.log("[SeamDebug] ctor:globals", {
                hasScene: Boolean(seamDebugWindow.__wa_scene),
                hasCamera: Boolean(seamDebugWindow.__wa_camera),
            });
        }

        // Default to rounded camera pixels for stable follow/focus rendering.
        // Exploration mode overrides this to keep seam mitigation behavior.
        this.camera.roundPixels = true;
        this.cameraLocked = false;
        this.zoomLocked = false;

        this.waScaleManager = waScaleManager;

        this.initCamera();
        this.hookCameraPreRender();

        this.bindEventHandlers();

        this.scene.events.on(Phaser.Scenes.Events.PRE_RENDER, this.preRenderSnap);
        this.scene.events.on(Phaser.Scenes.Events.RENDER, this.postRenderDebug);

        // Subscribe to map editor mode store to change camera bounds when the map editor is opened or closed
        this.unsubscribeMapEditorModeStore = mapEditorModeStore.subscribe((isOpened) => {
            this.mapEditorModeOpened = isOpened;
            // Define new bounds for camera if the map editor is opened
            if (isOpened) {
                this.camera.setBounds(0, 0, this.mapSize.width * 2, this.mapSize.height);
                this.lastAppliedMapBoundsSignature = undefined;
            } else {
                if (this.cameraMode === CameraMode.Exploration && this.explorationAllowOutsideMap) {
                    return;
                }
                this.applyMapBoundsWithSlack();
                if (this.cameraMode === CameraMode.Exploration) {
                    this.clampExplorerFocus();
                }
            }
        });

        this.scene.cameras.main.on(Phaser.Cameras.Scene2D.Events.PAN_START, () => {
            this.animationInProgress = true;
        });
        this.scene.cameras.main.on(Phaser.Cameras.Scene2D.Events.PAN_COMPLETE, () => {
            this.animationInProgress = false;
        });

        // Set zoom out to the maximum possible value (fit-to-map)
        this.refreshZoomBounds();
        this.targetZoomModifier = undefined;
        this.logSeamDebug("ctor:init", {
            mapW: this.mapSize.width,
            mapH: this.mapSize.height,
            cameraW: this.camera.width,
            cameraH: this.camera.height,
            cameraZoom: this.camera.zoom,
        });
    }

    public destroy(): void {
        this.scene.game.events.off(WaScaleManagerEvent.RefreshFocusOnTarget);
        this.camera.off("followupdate", this.onFollowUpdate);
        this.unsubscribeMapEditorModeStore();
        this.scene.events.off(Phaser.Scenes.Events.PRE_RENDER, this.preRenderSnap);
        this.scene.events.off(Phaser.Scenes.Events.RENDER, this.postRenderDebug);
        this.unhookCameraPreRender?.();
        this.unhookCameraPreRender = undefined;

        super.destroy();
    }

    public getCamera(): Phaser.Cameras.Scene2D.Camera {
        return this.camera;
    }

    /**
     * Set camera view to specific destination without changing current camera mode. Won't work if camera mode is set to Focus.
     * @param setTo Viewport on which the camera should set the position
     * @param duration Time for the transition im MS. If set to 0, transition will occur immediately
     */
    public setPosition(setTo: WaScaleManagerFocusTarget, duration = 1000): void {
        if (this.cameraMode === CameraMode.Focus) {
            return;
        }
        this.setCameraMode(CameraMode.Positioned);
        this.waScaleManager.saveZoom();
        this.camera.stopFollow();

        const currentZoomModifier = this.waScaleManager.zoomModifier;
        const zoomModifierChange = this.getZoomModifierChange(setTo.width, setTo.height);

        if (duration === 0) {
            this.waScaleManager.setRuntimeZoomModifier(currentZoomModifier + zoomModifierChange, this.camera);
            this.camera.centerOn(setTo.x, setTo.y);
            this.emit(CameraManagerEvent.CameraUpdate, this.getCameraUpdateEventData());
            this.playerToFollow?.once(hasMovedEventName, () => {
                if (this.playerToFollow) {
                    this.startFollowPlayer(this.playerToFollow, duration);
                }
            });
            return;
        }
        this.stopPan();
        this.camera.pan(setTo.x, setTo.y, duration, Easing.SineEaseOut, true, (camera, progress, x, y) => {
            if (this.cameraMode === CameraMode.Positioned) {
                if (zoomModifierChange !== 0) {
                    this.waScaleManager.setRuntimeZoomModifier(
                        currentZoomModifier + progress * zoomModifierChange,
                        this.camera
                    );
                }
                this.emit(CameraManagerEvent.CameraUpdate, this.getCameraUpdateEventData());
            }
            if (progress === 1) {
                this.playerToFollow?.once(hasMovedEventName, () => {
                    if (this.playerToFollow) {
                        this.startFollowPlayer(this.playerToFollow, duration);
                    }
                });
            }
        });
    }

    /**
     * Set camera to focus mode. As long as the camera is in the Focus mode, its view cannot be changed.
     * @param setTo Viewport on which the camera should focus on
     * @param duration Time for the transition im MS. If set to 0, transition will occur immediately
     */
    public enterFocusMode(focusOn: WaScaleManagerFocusTarget, margin = 0, duration = 1000): void {
        this.logSeamDebug("focus:enter", {
            focusX: focusOn.x,
            focusY: focusOn.y,
            focusW: focusOn.width,
            focusH: focusOn.height,
            margin,
            duration,
        });
        this.setCameraMode(CameraMode.Focus);
        this.waScaleManager.saveZoom();
        this.waScaleManager.setFocusTarget(focusOn);

        this.cameraLocked = false;
        this.zoomLocked = false;

        this.restoreZoomTween?.stop();
        this.startFollowTween?.stop();
        this.followTransitionInProgress = false;

        //Set the camera to focus on the given point
        const focusPoint = {
            x: focusOn.x,
            y: focusOn.y,
        };

        this.camera.startFollow(focusPoint, true);
        this.playerToFollow = undefined;

        const currentZoomModifier = this.waScaleManager.zoomModifier;
        const zoomModifierChange = this.getZoomModifierChange(focusOn.width, focusOn.height, 1 + margin);

        if (duration === 0) {
            this.waScaleManager.setRuntimeZoomModifier(currentZoomModifier + zoomModifierChange, this.camera);
            this.camera.centerOn(focusOn.x, focusOn.y);
            this.emit(CameraManagerEvent.CameraUpdate, this.getCameraUpdateEventData());
            return;
        }
        this.stopPan();
        this.camera.pan(focusOn.x, focusOn.y, duration, Easing.SineEaseOut, true, (camera, progress, x, y) => {
            if (zoomModifierChange) {
                this.waScaleManager.setRuntimeZoomModifier(
                    currentZoomModifier + progress * zoomModifierChange,
                    this.camera
                );
            }
            if (progress === 1) {
                // NOTE: Making sure the last action will be centering after zoom change
                this.camera.centerOn(focusOn.x, focusOn.y);
            }
            this.emit(CameraManagerEvent.CameraUpdate, this.getCameraUpdateEventData());
        });
    }

    public leaveFocusMode(player: Player, duration = 0): void {
        this.logSeamDebug("focus:leave", {
            playerX: player.x,
            playerY: player.y,
            duration,
        });
        this.waScaleManager.setFocusTarget();
        this.unlockCameraWithDelay(duration);
        this.startFollowPlayer(player, duration);
        this.restoreZoom(duration);
    }

    public move(moveEvents: ActiveEventList): void {
        let sendViewportUpdate = false;
        if (moveEvents.get(UserInputEvent.MoveUp)) {
            this.explorerFocusOn.y -= this.EDITOR_MODE_SCROLL_SPEED;
            this.clampExplorerFocus();
            this.scene.markDirty();
            sendViewportUpdate = true;
        } else if (moveEvents.get(UserInputEvent.MoveDown)) {
            this.explorerFocusOn.y += this.EDITOR_MODE_SCROLL_SPEED;
            this.clampExplorerFocus();
            this.scene.markDirty();
            sendViewportUpdate = true;
        }

        if (moveEvents.get(UserInputEvent.MoveLeft)) {
            this.explorerFocusOn.x -= this.EDITOR_MODE_SCROLL_SPEED;
            this.clampExplorerFocus();
            this.scene.markDirty();
            sendViewportUpdate = true;
        } else if (moveEvents.get(UserInputEvent.MoveRight)) {
            this.explorerFocusOn.x += this.EDITOR_MODE_SCROLL_SPEED;
            this.clampExplorerFocus();
            this.scene.markDirty();
            sendViewportUpdate = true;
        }

        if (sendViewportUpdate) {
            this.scene.sendViewportToServer();
        }
    }

    public startFollowPlayer(
        player: Player | RemotePlayer,
        duration = 0,
        targetZoomLevel: number | undefined = undefined,
        options: StartFollowPlayerOptions = {}
    ): void {
        this.logSeamDebug("follow:start", {
            duration,
            targetZoomLevel,
            playerX: player.x,
            playerY: player.y,
            preserveZoomOnComplete: options.preserveZoomOnComplete ?? false,
            freezeTargetDuringTransition: options.freezeTargetDuringTransition ?? false,
            smoothCatchUpMs: options.smoothCatchUpMs ?? 0,
        });
        const preserveZoomOnComplete = options.preserveZoomOnComplete ?? false;
        const freezeTargetDuringTransition = options.freezeTargetDuringTransition ?? false;
        const smoothCatchUpMs = options.smoothCatchUpMs ?? 0;

        if (duration > 0 && this.followTransitionInProgress && this.playerToFollow === player) {
            this.logSeamDebug("follow:skip-already-in-progress", {
                duration,
                playerX: player.x,
                playerY: player.y,
            });
            return;
        }

        if (this.startFollowTween) {
            this.startFollowTween.stop();
            this.startFollowTween.destroy();
            this.startFollowTween = undefined;
        }
        this.followTransitionInProgress = false;

        this.playerToFollow = player;
        if (duration === 0) {
            this.setCameraMode(CameraMode.Follow);
            this.camera.startFollow(player, true);
            this.logSeamDebug("follow:immediate-applied", {
                playerX: player.x,
                playerY: player.y,
            });
            this.scene.markDirty();
            this.applyMapBoundsWithSlack();
            this.refreshZoomBounds();
            if (!preserveZoomOnComplete) {
                this.snapCurrentZoomToDiscreteLevel();
            }
            return;
        }

        // Smooth transition to follow mode: if we are already in exploration mode, do not reinitialize it.
        // Reinitializing exploration causes a visible camera reset before the tween starts.
        const alreadyInExploration = this.cameraMode === CameraMode.Exploration && !!this.explorerFocusOn;
        if (!alreadyInExploration) {
            this.setExplorationMode();
        }
        if (!this.explorerFocusOn) {
            this.explorerFocusOn = {
                x: this.camera.scrollX + this.camera.width / 2,
                y: this.camera.scrollY + this.camera.height / 2,
            };
            this.camera.startFollow(this.explorerFocusOn, true);
        }

        const oldPos = { ...this.explorerFocusOn };
        const frozenTarget = freezeTargetDuringTransition ? { x: player.x, y: player.y } : undefined;
        const startZoomModifier = this.waScaleManager.zoomModifier;
        this.followTransitionInProgress = true;
        this.animationInProgress = true;
        this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.animateCallback);
        this.targetReachInProgress = false;
        this.explorerFocusOnTarget = undefined;
        this.stopPan();
        this.startFollowTween = this.scene.tweens.addCounter({
            from: 0,
            to: 1,
            duration,
            ease: Easing.SineEaseOut,
            onUpdate: (tween: Phaser.Tweens.Tween) => {
                if (!this.playerToFollow) {
                    return;
                }
                const followTarget = frozenTarget ?? this.playerToFollow;
                const progress = tween.getValue() ?? 0;
                const shiftX = (followTarget.x - oldPos.x) * progress;
                const shiftY = (followTarget.y - oldPos.y) * progress;
                this.explorerFocusOn.x = oldPos.x + shiftX;
                this.explorerFocusOn.y = oldPos.y + shiftY;
                if (targetZoomLevel !== undefined) {
                    this.waScaleManager.setRuntimeZoomModifier(
                        (targetZoomLevel - startZoomModifier) * progress + startZoomModifier,
                        this.camera
                    );
                }

                this.emit(CameraManagerEvent.CameraUpdate, this.getCameraUpdateEventData());
            },
            onComplete: () => {
                this.setCameraMode(CameraMode.Follow);
                if (smoothCatchUpMs > 0) {
                    this.camera.startFollow(player, true, 0.2, 0.2);
                    this.scene.time.delayedCall(smoothCatchUpMs, () => {
                        if (this.playerToFollow !== player || this.cameraMode !== CameraMode.Follow) {
                            return;
                        }
                        this.camera.startFollow(player, true);
                    });
                } else {
                    this.camera.startFollow(player, true);
                }
                this.animationInProgress = false;
                this.applyMapBoundsWithSlack();
                this.refreshZoomBounds();
                if (!preserveZoomOnComplete) {
                    this.snapCurrentZoomToDiscreteLevel();
                }
                this.followTransitionInProgress = false;
                this.startFollowTween = undefined;
                this.logSeamDebug("follow:complete", {
                    playerX: player.x,
                    playerY: player.y,
                    smoothCatchUpMs,
                    preserveZoomOnComplete,
                });
            },
        });
    }

    /**
     * Follow a remote player by their UUID. Centers the camera on them and shows a popup.
     */
    public followRemotePlayer(userUuid: string): void {
        // Find the remote player by UUID
        let remotePlayer = null;
        for (const [, player] of this.scene.MapPlayersByKey) {
            if (player.userUuid === userUuid) {
                remotePlayer = player;
                break;
            }
        }

        if (!remotePlayer) {
            console.warn(`Remote player with UUID ${userUuid} not found`);
            return;
        }

        // Restore camera mode
        this.startFollowPlayer(remotePlayer, 1000);
    }

    /**
     * Stop following a remote player.
     */
    public stopFollowRemotePlayer(): void {
        // Start following the current player
        this.startFollowPlayer(this.scene.CurrentPlayer, 1000);
    }

    /**
     * Updates the offset of the character compared to the center of the screen according to the layout manager
     * (tries to put the character in the center of the remaining space if there is a discussion going on.
     */
    public updateCameraOffset(box: Box, instant = false): void {
        if (this.cameraMode !== CameraMode.Follow || box.xEnd === undefined || box.yEnd === undefined) {
            return;
        }
        const xCenter = (box.xEnd - box.xStart) / 2 + box.xStart;
        const yCenter = (box.yEnd - box.yStart) / 2 + box.yStart;

        const game = HtmlUtils.querySelectorOrFail<HTMLCanvasElement>("#game canvas");

        // Let's put this in Game coordinates by applying the zoom level:
        let followOffsetX = (xCenter - game.offsetWidth / 2) / this.scene.scale.zoom;
        let followOffsetY = (yCenter - game.offsetHeight / 2) / this.scene.scale.zoom;
        const eff = this.getEffPixelsPerWorldUnit();
        if (eff <= 1.000001) {
            const q = 1 / eff;
            followOffsetX = Math.round(followOffsetX / q) * q;
            followOffsetY = Math.round(followOffsetY / q) * q;
        }

        if (instant) {
            this.camera.setFollowOffset(followOffsetX, followOffsetY);
            this.scene.markDirty();
            return;
        }

        const oldFollowOffsetX = this.camera.followOffset.x;
        const oldFollowOffsetY = this.camera.followOffset.y;

        this.animationInProgress = true;
        if (this.cameraOffsetCurrentTween) {
            this.cameraOffsetCurrentTween.stop();
            this.cameraOffsetCurrentTween.destroy();
            this.cameraOffsetCurrentTween = undefined;
        }
        this.cameraOffsetCurrentTween = this.scene.tweens.addCounter({
            from: 0,
            to: 1,
            duration: 500,
            ease: Easing.QuadEaseOut,
            onStart: () => {
                this.animationInProgress = true;
            },
            onUpdate: (tween) => {
                const progress = tween.getValue() ?? 0;
                const newOffsetX = oldFollowOffsetX + (followOffsetX - oldFollowOffsetX) * progress;
                const newOffsetY = oldFollowOffsetY + (followOffsetY - oldFollowOffsetY) * progress;
                this.camera.setFollowOffset(newOffsetX, newOffsetY);
                this.scene.markDirty();
            },
            onComplete: () => {
                this.animationInProgress = false;
            },
        });
    }

    public isCameraLocked(): boolean {
        return this.cameraLocked;
    }

    public isZoomLocked(): boolean {
        return this.isCameraLocked() || this.zoomLocked;
    }

    private getZoomModifierChange(width?: number, height?: number, multiplier = 1): number {
        if (!width || !height) {
            return 0;
        }
        const targetZoomModifier = this.waScaleManager.getTargetZoomModifierFor(
            width * multiplier,
            height * multiplier
        );
        const currentZoomModifier = this.waScaleManager.zoomModifier;
        return targetZoomModifier - currentZoomModifier;
    }

    public unlockCameraWithDelay(delay: number): void {
        this.scene.time.delayedCall(delay, () => {
            this.cameraLocked = false;
        });
    }

    public lockZoom(): void {
        this.zoomLocked = true;
    }

    public unlockZoom(): void {
        this.zoomLocked = false;
    }

    private setCameraMode(mode: CameraMode): void {
        if (this.cameraMode === mode) {
            return;
        }
        const previousMode = this.cameraMode;
        this.cameraMode = mode;
        this.logSeamDebug("mode:changed", {
            from: previousMode,
            to: mode,
        });
        this.updateRoundPixelsForMode();
    }

    public isInExplorationMode(): boolean {
        return this.cameraMode === CameraMode.Exploration;
    }

    public isFollowTransitionInProgress(): boolean {
        return this.followTransitionInProgress;
    }

    public isFollowingAnotherPlayerThanCurrent(): boolean {
        return (
            this.cameraMode === CameraMode.Follow &&
            this.playerToFollow !== undefined &&
            this.playerToFollow !== this.scene.CurrentPlayer
        );
    }

    private restoreZoom(duration = 0): void {
        if (duration === 0) {
            this.waScaleManager.setRuntimeZoomModifier(this.waScaleManager.getSaveZoom(), this.camera);
            return;
        }
        this.animationInProgress = true;
        this.restoreZoomTween?.stop();
        this.restoreZoomTween = this.scene.tweens.addCounter({
            from: this.waScaleManager.zoomModifier,
            to: this.waScaleManager.getSaveZoom(),
            duration,
            ease: Easing.SineEaseOut,
            onUpdate: (tween: Phaser.Tweens.Tween) => {
                this.waScaleManager.setRuntimeZoomModifier(tween.getValue() ?? 0, this.camera);
                this.emit(CameraManagerEvent.CameraUpdate, this.getCameraUpdateEventData());
            },
            onComplete: () => {
                this.animationInProgress = false;
            },
        });
    }

    private initCamera() {
        this.camera = this.scene.cameras.main;
        this.applyMapBoundsWithSlack();
    }

    private onFollowUpdate = () => {
        this.emit(CameraManagerEvent.CameraUpdate, this.getCameraUpdateEventData());
    };

    private bindEventHandlers(): void {
        this.scene.game.events.on(
            WaScaleManagerEvent.RefreshFocusOnTarget,
            (focusOn: { x: number; y: number; width: number; height: number }) => {
                if (!focusOn) {
                    return;
                }

                this.camera.centerOn(focusOn.x, focusOn.y);

                this.emit(CameraManagerEvent.CameraUpdate, this.getCameraUpdateEventData());
            }
        );

        this.camera.on("followupdate", this.onFollowUpdate);
    }

    private getCameraUpdateEventData(): CameraManagerEventCameraUpdateData {
        return {
            x: this.camera.worldView.x,
            y: this.camera.worldView.y,
            width: this.camera.worldView.width,
            height: this.camera.worldView.height,
            zoom: this.camera.scaleManager.zoom,
        };
    }

    // Create function to define the camera on exploration mode. The camera can be moved anywhere on the map. The camera is not locked on the player. The camera can be zoomed in and out. The camera can be moved with the mouse. The camera can be moved with the keyboard. The camera can be moved with the touchpad.
    public setExplorationMode(allowOutsideMap = true): void {
        this.logSeamDebug("exploration:enter", {
            allowOutsideMap,
            currentScrollX: this.camera.scrollX,
            currentScrollY: this.camera.scrollY,
        });
        this.cameraLocked = false;
        //this.stopFollow();
        this.setCameraMode(CameraMode.Exploration);
        this.explorationAllowOutsideMap = allowOutsideMap;

        this.camera.setFollowOffset(0, 0);

        if (allowOutsideMap) {
            this.camera.setBounds(
                -this.mapSize.width,
                -this.mapSize.height,
                this.mapSize.width * 3,
                this.mapSize.height * 3,
                false
            );
            this.lastAppliedMapBoundsSignature = undefined;
        } else {
            this.applyMapBoundsWithSlack();
        }

        this.explorerFocusOn = {
            x: this.camera.scrollX + this.camera.width / 2,
            y: this.camera.scrollY + this.camera.height / 2,
        };
        this.clampExplorerFocus();
        // In exploration mode, keep sub-pixel camera movement for smooth drag/pan.
        this.camera.startFollow(this.explorerFocusOn, true);

        this.refreshZoomBounds();
        this.snapCurrentZoomToDiscreteLevel();
        this.logSeamDebug("exploration:ready", {
            allowOutsideMap,
            explorerX: this.explorerFocusOn.x,
            explorerY: this.explorerFocusOn.y,
        });

        // Center the camera on the player
        //this.scene.cameras.main.centerOn(this.scene.CurrentPlayer.x, this.scene.CurrentPlayer.y);

        //const targetZoomModifier = this.waScaleManager.getTargetZoomModifierFor(mapWidth, mapHeight);
        //this.waScaleManager.maxZoomOut = targetZoomModifier;
    }

    public refreshZoomBounds(): void {
        this.waScaleManager.maxZoomOut = this.getFitZoomModifier();
    }

    public setResizeInProgress(isInProgress: boolean): void {
        this.resizeInProgress = isInProgress;
        this.logSeamDebug("resize:flag", {
            isInProgress,
            wheelAccumulator: this.wheelZoomAccumulator,
        });
        if (isInProgress) {
            this.wheelZoomAccumulator = 0;
            this.cancelSmoothZoomAndAnchor();
        }
    }

    public getZoomModifier(): number {
        return this.waScaleManager.zoomModifier;
    }

    public captureZoomStateSnapshot(): CameraZoomStateSnapshot {
        const minCameraZoom = this.getMinimumCameraZoomForCurrentView();
        const maxCameraZoom = this.getMaximumCameraZoomForCurrentView();
        const levels = this.buildDiscreteCameraZoomLevels(minCameraZoom, maxCameraZoom);

        const currentCameraZoom = Clamp(this.camera.zoom, minCameraZoom, maxCameraZoom);
        const index = this.getClosestZoomLevelIndex(levels, currentCameraZoom);
        const normalizedDiscreteLevel = levels.length <= 1 ? 0 : index / (levels.length - 1);

        const scaleZoom = this.scene.scale.zoom || 1;

        return {
            cameraZoom: currentCameraZoom,
            normalizedDiscreteLevel,
            effectiveCssZoom: currentCameraZoom * scaleZoom,
        };
    }

    public restoreZoomStateSnapshotAfterResize(snapshot: CameraZoomStateSnapshot): void {
        if (!snapshot || !Number.isFinite(snapshot.cameraZoom)) {
            this.snapCurrentZoomToDiscreteLevel();
            return;
        }

        this.cancelSmoothZoomAndAnchor();

        const minCameraZoom = this.getMinimumCameraZoomForCurrentView();
        const maxCameraZoom = this.getMaximumCameraZoomForCurrentView();
        const levels = this.buildDiscreteCameraZoomLevels(minCameraZoom, maxCameraZoom);

        const scaleZoomNow = this.scene.scale.zoom || 1;

        // NEW: preserve effective css zoom across resize
        let targetCameraZoom = snapshot.effectiveCssZoom
            ? snapshot.effectiveCssZoom / scaleZoomNow
            : snapshot.cameraZoom;

        targetCameraZoom = this.quantizeCameraZoomToTileGrid(targetCameraZoom);

        targetCameraZoom = Clamp(targetCameraZoom, minCameraZoom, maxCameraZoom);

        if (levels.length > 0) {
            const idx = this.getClosestZoomLevelIndex(levels, targetCameraZoom);
            targetCameraZoom = levels[idx] ?? targetCameraZoom;
        }

        targetCameraZoom = this.quantizeCameraZoomToTileGrid(targetCameraZoom);

        this.logSeamDebug("resize:restore-zoom", {
            snapshotCameraZoom: snapshot.cameraZoom,
            snapshotCssZoom: snapshot.effectiveCssZoom,
            minCameraZoom,
            maxCameraZoom,
            levelsCount: levels.length,
            targetCameraZoom,
        });

        if (Math.abs(this.camera.zoom - targetCameraZoom) > 0.000001) {
            this.waScaleManager.setRuntimeCameraZoom(targetCameraZoom, this.camera);
        }

        if (this.cameraMode === CameraMode.Exploration) {
            this.clampExplorerFocus();
        }

        this.scene.markDirty();
        this.emit(CameraManagerEvent.CameraUpdate, this.getCameraUpdateEventData());
    }

    public snapCurrentZoomToDiscreteLevel(): void {
        const minCameraZoom = this.getMinimumCameraZoomForCurrentView();
        const maxCameraZoom = this.getMaximumCameraZoomForCurrentView();
        const levels = this.buildDiscreteCameraZoomLevels(minCameraZoom, maxCameraZoom);
        const currentCameraZoom = Clamp(this.camera.zoom, minCameraZoom, maxCameraZoom);
        const closestIndex = this.getClosestZoomLevelIndex(levels, currentCameraZoom);
        const snappedCameraZoom = levels[closestIndex] ?? currentCameraZoom;

        if (Math.abs(snappedCameraZoom - this.camera.zoom) <= 0.000001) {
            return;
        }

        this.waScaleManager.setRuntimeCameraZoom(snappedCameraZoom, this.camera);
        if (this.cameraMode === CameraMode.Exploration) {
            this.clampExplorerFocus();
        }
        this.scene.markDirty();
        this.emit(CameraManagerEvent.CameraUpdate, this.getCameraUpdateEventData());
    }

    private getFitCameraZoom(): number {
        const safeMapWidth = Math.max(this.mapSize.width, 1);
        const safeMapHeight = Math.max(this.mapSize.height - this.getFitExcludeTopWorld(), 1);
        return Math.max(Math.min(this.camera.width / safeMapWidth, this.camera.height / safeMapHeight), Number.EPSILON);
    }

    private getFitZoomModifier(): number {
        return this.waScaleManager.cameraZoomToZoomModifier(this.getFitCameraZoom());
    }

    private hookCameraPreRender(): void {
        const camera = this.camera as Phaser.Cameras.Scene2D.Camera & {
            preRender: () => void;
        };
        const originalPreRender = camera.preRender.bind(camera);

        camera.preRender = () => {
            this.syncRoundPixelsForModeAndZoom("camera_pre_render:before");
            originalPreRender();
            this.logSeamDebug("camera_pre_render:after");
            try {
                this.scene.getGameMapFrontWrapper().applyRenderPhaseShim(camera, "camera_pre_render");
            } catch (error) {
                if (this.isSeamDebugEnabled()) {
                    console.log("[SeamDebug] layer-shim:apply:error", error);
                }
            }
        };

        this.unhookCameraPreRender = () => {
            camera.preRender = originalPreRender;
        };
    }

    private preRenderSnap = () => {
        this.syncFitZoomConstraints();
        this.syncMapBoundsWithDevToolsOverrides();
        // Scene PRE_RENDER happens before camera.preRender; this is only for diagnostics.
        this.logSeamDebug("pre_render:start");
    };

    private postRenderDebug = () => {
        try {
            this.scene.getGameMapFrontWrapper().clearRenderPhaseShim("post_render");
        } catch (error) {
            if (this.isSeamDebugEnabled()) {
                console.log("[SeamDebug] layer-shim:clear:error", error);
            }
        }
        this.logSeamDebug("post_render");
    };

    private quantizeCameraZoomToTileGrid(cameraZoom: number): number {
        const tileSize = 32;
        const { rx, ry } = this.getCanvasBufferRatios();

        // If rx != ry (happens when CSS size becomes fractional / rounded differently),
        // you *cannot* perfectly satisfy both axes with one zoom value.
        // Still, using the average ratio makes it far more stable than using camera.width.
        const r = (rx + ry) * 0.5;

        const eff = cameraZoom * r;
        const k = Math.round(tileSize * eff);
        const effQ = k / tileSize;
        const result = effQ / r;
        this.logSeamDebug("zoom:quantize-tile-grid", {
            cameraZoom,
            tileSize,
            rx,
            ry,
            avgRatio: r,
            effBefore: eff,
            effQuantized: effQ,
            k,
            result,
        });

        return result;
    }

    private quantizeMinZoomToTileGrid(minZoom: number): number {
        const dpr = window.devicePixelRatio ?? 1;
        const scaleZoom = this.scene.scale.zoom || 1; // ScaleManager zoom
        const tileSize = 32; // <-- set to your actual tile size

        // Convert camera zoom -> effective screen scale
        const eff = minZoom * scaleZoom * dpr;

        // Choose the smallest eff >= current that makes tileSize*eff integer
        const k = Math.ceil(tileSize * eff);
        const effQuant = k / tileSize;

        // Convert back to camera zoom
        return effQuant / (scaleZoom * dpr);
    }

    private getSafeFitCameraZoom(): number {
        const fit = this.getFitCameraZoom();
        const maxCameraZoom = this.getMaximumCameraZoomForCurrentView();

        // you already keep it slightly zoomed-in vs fit (good)
        const safe = Math.min(fit * this.SAFE_MIN_ZOOM_OUT_FACTOR, maxCameraZoom);

        // NEW: quantize so pixel art minification doesn't get a cursed ratio
        return Math.min(this.quantizeMinZoomToTileGrid(safe), maxCameraZoom);
    }

    private getMinimumCameraZoomForCurrentView(): number {
        const fitMinimum = this.getSafeFitCameraZoom();
        if (this.cameraMode === CameraMode.Exploration) {
            return Math.min(fitMinimum, this.getMaximumCameraZoomForCurrentView());
        }
        let minimum = fitMinimum;

        if (this.cameraMode !== CameraMode.Follow) {
            return minimum;
        }

        const followMinimum = fitMinimum * this.FOLLOW_MIN_ZOOM_MARGIN;
        minimum = Math.max(minimum, followMinimum);
        return Math.min(minimum, this.getMaximumCameraZoomForCurrentView());
    }

    private getMaximumCameraZoomForCurrentView(): number {
        return this.waScaleManager.zoomModifierToCameraZoom(this.getMaximumZoomModifierForCurrentView());
    }

    private getMinimumZoomModifierForCurrentView(): number {
        return this.waScaleManager.cameraZoomToZoomModifier(this.getMinimumCameraZoomForCurrentView());
    }

    private getMaximumZoomModifierForCurrentView(): number {
        return this.waScaleManager.getMaximumZoomModifierForCurrentView();
    }

    private buildDiscreteZoomLevels(minZoomModifier: number, maxZoomModifier: number): number[] {
        if (
            !Number.isFinite(minZoomModifier) ||
            !Number.isFinite(maxZoomModifier) ||
            minZoomModifier <= 0 ||
            maxZoomModifier <= 0
        ) {
            return [this.waScaleManager.zoomModifier];
        }

        if (maxZoomModifier <= minZoomModifier) {
            return [minZoomModifier];
        }

        const levels: number[] = [];
        const minLog = Math.log(minZoomModifier);
        const maxLog = Math.log(maxZoomModifier);
        const denom = this.DISCRETE_ZOOM_LEVEL_COUNT - 1;

        for (let i = 0; i < this.DISCRETE_ZOOM_LEVEL_COUNT; i++) {
            const t = i / denom;
            levels.push(Math.exp(minLog + t * (maxLog - minLog)));
        }

        return levels;
    }

    private buildDiscreteCameraZoomLevels(minCameraZoom: number, maxCameraZoom: number): number[] {
        const levels = this.buildDiscreteZoomLevels(minCameraZoom, maxCameraZoom);
        const quantized = levels.map((z) => this.quantizeCameraZoomToTileGrid(z));

        // de-dup (quantization can create duplicates)
        const unique: number[] = [];
        for (const z of quantized) {
            if (unique.length === 0 || Math.abs(unique[unique.length - 1] - z) > 1e-6) unique.push(z);
        }

        if (unique.length <= 4 || this.DISCRETE_EDGE_SKIP_LEVELS <= 0) {
            this.logSeamDebug("zoom-levels:built", {
                minCameraZoom,
                maxCameraZoom,
                rawCount: levels.length,
                uniqueCount: unique.length,
                first: unique[0],
                last: unique[unique.length - 1],
            });
            return unique;
        }

        const safeSkip = Clamp(this.DISCRETE_EDGE_SKIP_LEVELS, 0, unique.length - 1);
        const sliced = unique.slice(safeSkip);
        this.logSeamDebug("zoom-levels:sliced", {
            minCameraZoom,
            maxCameraZoom,
            rawCount: levels.length,
            uniqueCount: unique.length,
            safeSkip,
            slicedCount: sliced.length,
            first: sliced[0],
            last: sliced[sliced.length - 1],
        });
        return sliced;
    }

    private getClosestZoomLevelIndex(levels: number[], zoomModifier: number): number {
        let closestIndex = 0;
        let closestDistance = Number.POSITIVE_INFINITY;
        for (let i = 0; i < levels.length; i++) {
            const distance = Math.abs(levels[i] - zoomModifier);
            if (distance < closestDistance) {
                closestDistance = distance;
                closestIndex = i;
            }
        }
        return closestIndex;
    }

    private normalizeWheelDelta(deltaY: number): number {
        // Phaser already gives us pixel-like deltas in most environments.
        // Clamp outliers so stalled frames don't cause giant zoom jumps.
        return Clamp(deltaY, -1000, 1000);
    }

    public zoomByWheelDelta(deltaY: number, smooth: boolean): void {
        if (this.resizeInProgress || this.isZoomLocked() || !Number.isFinite(deltaY) || deltaY === 0) {
            this.logSeamDebug("wheel:ignored", {
                deltaY,
                smooth,
                resizeInProgress: this.resizeInProgress,
                zoomLocked: this.isZoomLocked(),
                finite: Number.isFinite(deltaY),
            });
            return;
        }

        const normalizedDelta = this.normalizeWheelDelta(deltaY);
        this.wheelZoomAccumulator += normalizedDelta;

        let steps = 0;
        while (Math.abs(this.wheelZoomAccumulator) >= this.NORMALIZED_WHEEL_STEP) {
            const direction = Math.sign(this.wheelZoomAccumulator);
            this.wheelZoomAccumulator -= direction * this.NORMALIZED_WHEEL_STEP;
            steps += direction;
            if (Math.abs(steps) >= this.MAX_WHEEL_STEPS_PER_EVENT) {
                break;
            }
        }

        if (steps === 0) {
            this.logSeamDebug("wheel:no-step", {
                deltaY,
                normalizedDelta,
                wheelAccumulator: this.wheelZoomAccumulator,
            });
            return;
        }

        const minCameraZoom = this.getMinimumCameraZoomForCurrentView();
        const maxCameraZoom = this.getMaximumCameraZoomForCurrentView();
        const levels = this.buildDiscreteCameraZoomLevels(minCameraZoom, maxCameraZoom);
        const currentCameraZoom = Clamp(this.camera.zoom, minCameraZoom, maxCameraZoom);
        const currentIndex = this.getClosestZoomLevelIndex(levels, currentCameraZoom);

        // Positive wheel delta means zoom-out in browser semantics.
        const nextIndex = Clamp(currentIndex - steps, 0, levels.length - 1);
        if (nextIndex === currentIndex) {
            this.logSeamDebug("wheel:clamped-to-same-index", {
                steps,
                currentIndex,
                nextIndex,
                minCameraZoom,
                maxCameraZoom,
            });
            return;
        }

        const nextCameraZoom = this.quantizeCameraZoomToTileGrid(levels[nextIndex]);
        this.logZoomDebug(`wheel steps=${steps} nextZoom=${nextCameraZoom} smooth=${smooth}`);
        this.logSeamDebug("wheel:step", {
            deltaY,
            normalizedDelta,
            steps,
            currentIndex,
            nextIndex,
            currentCameraZoom,
            nextCameraZoom,
            levelsCount: levels.length,
            minCameraZoom,
            maxCameraZoom,
            wheelAccumulator: this.wheelZoomAccumulator,
            smooth,
        });

        if (smooth) {
            this.animateToZoomLevel(this.waScaleManager.cameraZoomToZoomModifier(nextCameraZoom));
        } else {
            this.waScaleManager.setRuntimeCameraZoom(nextCameraZoom, this.camera);
            // Camera matrices/worldView are not always fully coherent in the same tick as setZoom().
            // Defer anchor correction to the next update frame to keep cursor-anchored zoom reliable.
            this.startAnimation();
        }
    }

    public triggerMaxZoomOutAnimation(): void {
        const targetZoomModifier = this.getFitZoomModifier();

        this.startFollowTween?.stop();
        this.startFollowTween = undefined;
        this.followTransitionInProgress = false;
        this.animationInProgress = false;

        this.centerCameraOn({ x: this.mapSize.width / 2, y: this.mapSize.height / 2 }, targetZoomModifier);
    }

    private stopPan(): void {
        this.camera.panEffect.reset();
    }

    public centerCameraOn(position: { x: number; y: number }, zoom?: number): void {
        this.explorerFocusOnTarget = {
            ...position,
            zoom: zoom ?? this.waScaleManager.zoomModifier,
        };

        if (zoom === undefined && this.waScaleManager.zoomModifier < this._resistanceEndZoomLevel) {
            this.explorerFocusOnTarget.zoom = this._resistanceEndZoomLevel;
        }

        this.startAnimation();
    }

    /**
     * Zooms the camera by a factor passed in parameter.
     * Is the final zoom level is greater than the max zoom level, an animation will slowly bring back the camera to the max zoom level
     * (if no animation is currently running)
     *
     * Also, this supports the notion of "WALL".
     * The wall can be "in-place" or "broken". When the wall is in place, it is NOT possible to pass the resistance zone.
     * We do this by altering the zoom factor to make it slower as we are getting closer to the resistance zone end.
     *
     * When we get out of the resistance zone OR if we are in the resistance zone but zoom towards the start of the zone,
     * we break the wall for 10 seconds.
     */
    public zoomByFactor(zoomFactor: number, smooth: boolean): void {
        // Keep zoom-out clamped to map bounds (avoid blank space around the map)
        const minZoomModifier = this.getMinimumZoomModifierForCurrentView();
        const maxZoomModifier = this.getMaximumZoomModifierForCurrentView();

        const wallBroken = Date.now() - this.wallDownDate < 10000 || this.enableResistanceWall === false;
        if (
            this.isBetween(
                this.waScaleManager.zoomModifier,
                this._resistanceStartZoomLevel,
                this._resistanceEndZoomLevel
            )
        ) {
            if (
                !wallBroken &&
                ((this._resistanceEndZoomLevel < this._resistanceStartZoomLevel && zoomFactor < 1) ||
                    (this.resistanceEndZoomLevel > this.resistanceStartZoomLevel && zoomFactor > 1))
            ) {
                // Let's alter the zoom factor to make it slower if we are in the resistance zone.
                //const maxZoomFactor = this._resistanceEndZoomLevel / this.waScaleManager.zoomModifier;
                const lambda =
                    5 / ((this.resistanceEndZoomLevel - this.resistanceStartZoomLevel) * this._resistanceEndZoomLevel);

                const resultZoom =
                    this.resistanceEndZoomLevel - 1 / (lambda * this.waScaleManager.zoomModifier * zoomFactor);
                zoomFactor = resultZoom / this.waScaleManager.zoomModifier;
            } else {
                // We zoom in the opposite direction, let's break the wall for 10 seconds.
                this.wallDownDate = Date.now();
                debugZoom("Resistance wall is broken because we scrolled towards the start of the resistance zone");
            }
        }

        const targetZoom = this.waScaleManager.zoomModifier * zoomFactor;
        const clampedZoom = Clamp(targetZoom, minZoomModifier, maxZoomModifier);

        if (!smooth) {
            waScaleManager.setRuntimeZoomModifier(clampedZoom, this.camera);
            this.logZoomDebug("animate applied zoomModifier");
            this.applyZoomAnchor();
            this.clearZoomAnchor();
        } else {
            this.animateToZoomLevel(clampedZoom);
        }

        if (this.animationInProgress) {
            // Let's not trigger the resistance if the zoom in or out originates from an animation.
            return;
        }

        if (!this.resistanceCallback) {
            // If there is no resistance configured, let's return.
            return;
        }

        if (
            this.isBetween(
                this.waScaleManager.zoomModifier,
                this._resistanceStartZoomLevel,
                this._resistanceEndZoomLevel
            ) &&
            this.isCameraWithinWokaRadius()
        ) {
            if (!this.resistZoomCallback) {
                this.resistZoomCallback = this.resistZoom.bind(this);
                this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.resistZoomCallback);
                this.resistanceZoneEnterDate = Date.now();
            }
        }
    }

    private isCameraWithinWokaRadius(): boolean {
        if (this.resistanceRadiusAroundWoka === undefined || !this.player) {
            return true;
        }
        const cameraCenter = {
            x: this.camera.worldView.x + this.camera.worldView.width / 2,
            y: this.camera.worldView.y + this.camera.worldView.height / 2,
        };
        const distance = Math.sqrt(
            Math.pow(cameraCenter.x - this.player.x, 2) + Math.pow(cameraCenter.y - this.player.y, 2)
        );
        return distance < this.resistanceRadiusAroundWoka;
    }

    /**
     * Returns true if the value is between the two bounds (strictly).
     * The 2 bounds can be in any order.
     */
    private isBetween(value: number, bound1: number, bound2: number): boolean {
        return (value > bound1 && value < bound2) || (value > bound2 && value < bound1);
    }

    private animateToZoomLevel(targetZoomModifier: number): void {
        this.targetZoomModifier = targetZoomModifier;
        const currentZoomModifier = this.waScaleManager.cameraZoomToZoomModifier(this.camera.zoom || 1);
        this.targetDirection = this.targetZoomModifier > currentZoomModifier ? "zoom_in" : "zoom_out";
        this.startAnimation();
    }

    private startAnimation() {
        if (!this.targetReachInProgress) {
            this.targetReachInProgress = true;
            this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.animateCallback);
        }
    }

    private animate(time: number, delta: number): void {
        if (this.animationInProgress) {
            return;
        }
        this.logSeamDebug("animate:tick:start", {
            time,
            delta,
            hasTargetZoom: this.targetZoomModifier !== undefined,
            hasCameraSpeed: this.cameraSpeed !== undefined,
            hasExplorerTarget: this.explorerFocusOnTarget !== undefined,
            targetDirection: this.targetDirection,
            zoomModifier: this.waScaleManager.zoomModifier,
        });
        const minZoomModifier = this.getMinimumZoomModifierForCurrentView();
        const maxZoomModifier = this.getMaximumZoomModifierForCurrentView();
        if (this.targetZoomModifier !== undefined) {
            const targetZoomModifier = this.targetZoomModifier;
            const currentZoomModifier = this.waScaleManager.cameraZoomToZoomModifier(this.camera.zoom || 1);

            let newZoom =
                currentZoomModifier +
                (((targetZoomModifier - currentZoomModifier) * delta) / 100) * this.cameraZoomSpeed;

            if (Math.abs(targetZoomModifier - newZoom) <= 0.0001) {
                newZoom = targetZoomModifier;
                this.targetZoomModifier = undefined;
            } else if (this.targetDirection === "zoom_in" && newZoom >= this.targetZoomModifier) {
                newZoom = this.targetZoomModifier;
                this.targetZoomModifier = undefined;
            } else if (this.targetDirection === "zoom_out" && newZoom <= this.targetZoomModifier) {
                newZoom = this.targetZoomModifier;
                this.targetZoomModifier = undefined;
            }

            if (newZoom < minZoomModifier || newZoom > maxZoomModifier) {
                newZoom = Clamp(newZoom, minZoomModifier, maxZoomModifier);
                if (this.targetDirection === "zoom_out") {
                    this.targetZoomModifier = undefined;
                }
                if (this.targetDirection === "zoom_in") {
                    this.targetZoomModifier = undefined;
                }
            }

            waScaleManager.setRuntimeZoomModifier(newZoom, this.camera);
            this.clampExplorerFocus();
        }

        // Let's move the camera according to the speed
        if (this.cameraSpeed) {
            this.explorerFocusOn.x += (this.cameraSpeed.x * delta) / 400;
            this.explorerFocusOn.y += (this.cameraSpeed.y * delta) / 400;
            this.clampExplorerFocus();

            // Now, let's slow down the camera a bit
            this.cameraSpeed.x *= 1 - delta / 500;
            this.cameraSpeed.y *= 1 - delta / 500;
            if (Math.pow(this.cameraSpeed.x, 2) + Math.pow(this.cameraSpeed.y, 2) < 20) {
                this.cameraSpeed = undefined;
            }
        }

        if (this.explorerFocusOnTarget) {
            let newZoom =
                this.waScaleManager.zoomModifier +
                (((this.explorerFocusOnTarget.zoom - this.waScaleManager.zoomModifier) * delta) / 100) *
                    this.focusTargetSpeed;

            let x =
                this.explorerFocusOn.x +
                (((this.explorerFocusOnTarget.x - this.explorerFocusOn.x) * delta) / 100) * this.focusTargetSpeed;
            let y =
                this.explorerFocusOn.y +
                (((this.explorerFocusOnTarget.y - this.explorerFocusOn.y) * delta) / 100) * this.focusTargetSpeed;

            if (Math.abs(this.explorerFocusOnTarget.x - x) < 1 && Math.abs(this.explorerFocusOnTarget.y - y) < 1) {
                x = this.explorerFocusOnTarget.x;
                y = this.explorerFocusOnTarget.y;
                newZoom = this.explorerFocusOnTarget.zoom;
                this.explorerFocusOnTarget = undefined;
            }

            newZoom = Clamp(newZoom, minZoomModifier, maxZoomModifier);

            waScaleManager.setRuntimeZoomModifier(newZoom, this.camera);
            this.explorerFocusOn.x = x;
            this.explorerFocusOn.y = y;
            this.clampExplorerFocus();
        }

        this.applyZoomAnchor();

        if (
            this.cameraSpeed === undefined &&
            this.targetZoomModifier === undefined &&
            this.explorerFocusOnTarget === undefined
        ) {
            this.clearZoomAnchor();
            this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.animateCallback);
            this.targetReachInProgress = false;
            this.logSeamDebug("animate:tick:stop", {
                time,
                delta,
            });
        }

        this.logSeamDebug("animate:tick:end", {
            time,
            delta,
            zoomModifier: this.waScaleManager.zoomModifier,
            targetZoomModifier: this.targetZoomModifier,
            explorerTargetX: this.explorerFocusOnTarget?.x,
            explorerTargetY: this.explorerFocusOnTarget?.y,
            explorerTargetZoom: this.explorerFocusOnTarget?.zoom,
            cameraSpeedX: this.cameraSpeed?.x,
            cameraSpeedY: this.cameraSpeed?.y,
        });
        this.emit(CameraManagerEvent.CameraUpdate, this.getCameraUpdateEventData());
    }

    private getFramebufferPixelsPerScreenUnit(): number {
        const canvas = this.scene.sys.game.canvas;
        const camW = this.camera.width || 1; // "screen units" Phaser uses for camera
        return canvas.width / camW; // framebuffer px per camera screen unit
    }

    private getEffPixelsPerWorldUnit(): number {
        // framebuffer px per world unit
        return (this.camera.zoom || 1) * this.getFramebufferPixelsPerScreenUnit();
    }

    private getCameraBounds(): { x: number; y: number; width: number; height: number } | undefined {
        return (this.camera as unknown as { _bounds?: { x: number; y: number; width: number; height: number } })
            ._bounds;
    }

    private clampScrollToBoundsOnPixelGrid(eff: number): void {
        const bounds = this.getCameraBounds();
        if (!bounds) {
            this.logSeamDebug("clamp:no-bounds", { eff });
            return;
        }

        const zoom = this.camera.zoom || 1;
        const viewWidth = this.camera.width / zoom;
        const viewHeight = this.camera.height / zoom;

        const minScrollX = bounds.x;
        const minScrollY = bounds.y;
        const maxScrollX = Math.max(minScrollX, bounds.x + bounds.width - viewWidth);
        const maxScrollY = Math.max(minScrollY, bounds.y + bounds.height - viewHeight);

        const minXPx = Math.ceil(minScrollX * eff);
        const minYPx = Math.ceil(minScrollY * eff);
        const maxXPx = Math.floor(maxScrollX * eff);
        const maxYPx = Math.floor(maxScrollY * eff);

        // Stay one framebuffer pixel inside X bounds to avoid edge phase-lock seams.
        const insetMinXPx = maxXPx - minXPx >= this.BOUNDARY_INSET_PX * 2 ? minXPx + this.BOUNDARY_INSET_PX : minXPx;
        const insetMaxXPx = maxXPx - minXPx >= this.BOUNDARY_INSET_PX * 2 ? maxXPx - this.BOUNDARY_INSET_PX : maxXPx;

        const xPx = Clamp(Math.round(this.camera.scrollX * eff), insetMinXPx, insetMaxXPx);
        const yPx = Clamp(Math.round(this.camera.scrollY * eff), minYPx, maxYPx);

        this.logSeamDebug("clamp:computed", {
            eff,
            zoom,
            viewWidth,
            viewHeight,
            boundsX: bounds.x,
            boundsY: bounds.y,
            boundsW: bounds.width,
            boundsH: bounds.height,
            minScrollX,
            maxScrollX,
            minXPx,
            maxXPx,
            insetMinXPx,
            insetMaxXPx,
            scrollXPxBefore: this.camera.scrollX * eff,
            scrollYPxBefore: this.camera.scrollY * eff,
            xPx,
            yPx,
            atLeftBoundaryPx: xPx <= insetMinXPx,
            atRightBoundaryPx: xPx >= insetMaxXPx,
        });

        this.camera.scrollX = xPx / eff;
        this.camera.scrollY = yPx / eff;

        this.logSeamDebug("clamp:applied", {
            scrollX: this.camera.scrollX,
            scrollY: this.camera.scrollY,
            scrollXPx: this.camera.scrollX * eff,
            scrollYPx: this.camera.scrollY * eff,
        });
    }

    private clampScrollToBoundsContinuous(eff: number): void {
        const bounds = this.getCameraBounds();
        if (!bounds) {
            this.logSeamDebug("clamp2:no-bounds", { eff });
            return;
        }

        const zoom = this.camera.zoom || 1;
        const viewWidth = this.camera.width / zoom;
        const viewHeight = this.camera.height / zoom;

        const minScrollX = bounds.x;
        const minScrollY = bounds.y;
        const maxScrollX = Math.max(minScrollX, bounds.x + bounds.width - viewWidth);
        const maxScrollY = Math.max(minScrollY, bounds.y + bounds.height - viewHeight);

        const beforeX = this.camera.scrollX;
        const beforeY = this.camera.scrollY;

        // Keep this clamp continuous (no pixel-grid snapping/inset) so phase correction survives.
        this.camera.scrollX = Clamp(this.camera.scrollX, minScrollX, maxScrollX);
        this.camera.scrollY = Clamp(this.camera.scrollY, minScrollY, maxScrollY);

        this.logSeamDebug("clamp2:applied", {
            eff,
            zoom,
            viewWidth,
            viewHeight,
            minScrollX,
            maxScrollX,
            beforeX,
            beforeY,
            afterX: this.camera.scrollX,
            afterY: this.camera.scrollY,
            afterXPx: this.camera.scrollX * eff,
            afterYPx: this.camera.scrollY * eff,
        });
    }

    private alignRenderPhaseToPixelGrid(eff: number): void {
        const matrix = (this.camera as unknown as { matrix?: { tx: number; ty: number } }).matrix;
        if (!matrix) {
            this.logSeamDebug("phase:no-matrix", { eff });
            return;
        }

        const framebufferPerScreenUnit = this.getFramebufferPixelsPerScreenUnit();
        if (!Number.isFinite(framebufferPerScreenUnit) || framebufferPerScreenUnit <= 0) {
            return;
        }

        const txPx = matrix.tx * framebufferPerScreenUnit;
        const tyPx = matrix.ty * framebufferPerScreenUnit;

        const deltaXPx = Math.round(txPx) - txPx;
        const deltaYPx = Math.round(tyPx) - tyPx;

        this.logSeamDebug("phase:before", {
            eff,
            framebufferPerScreenUnit,
            tx: matrix.tx,
            ty: matrix.ty,
            txPx,
            tyPx,
            deltaXPx,
            deltaYPx,
        });

        if (Math.abs(deltaXPx) > 0.001) {
            this.camera.scrollX -= deltaXPx / eff;
        }
        if (Math.abs(deltaYPx) > 0.001) {
            this.camera.scrollY -= deltaYPx / eff;
        }

        this.logSeamDebug("phase:after", {
            scrollX: this.camera.scrollX,
            scrollY: this.camera.scrollY,
            scrollXPx: this.camera.scrollX * eff,
            scrollYPx: this.camera.scrollY * eff,
        });
    }

    private logZoomDebug(reason: string): void {
        if (!this.isSeamDebugEnabled()) {
            return;
        }
        const canvas = this.scene.sys.game.canvas;
        const rect = canvas.getBoundingClientRect();
        const bufferRatio = rect.width > 0 ? canvas.width / rect.width : 1;

        const camZoom = this.camera.zoom || 1;
        const eff = this.getEffPixelsPerWorldUnit();
        const tileSize = 32;

        console.log(`[zoom-debug] ${reason}`, {
            camZoom,
            scaleZoom: this.scene.scale.zoom,
            dpr: window.devicePixelRatio ?? 1,
            canvasW: canvas.width,
            canvasH: canvas.height,
            rectW: rect.width,
            rectH: rect.height,
            bufferRatio,
            eff,
            tilePx: tileSize * eff,
            tileFrac: (tileSize * eff) % 1,
            scrollX: this.camera.scrollX,
            scrollY: this.camera.scrollY,
            followOffX: this.camera.followOffset.x,
            followOffY: this.camera.followOffset.y,
            mode: this.cameraMode,
        });
    }

    private getCanvasBufferRatios(): { rx: number; ry: number; rectW: number; rectH: number } {
        const canvas = this.scene.sys.game.canvas;
        const rect = canvas.getBoundingClientRect();

        const rx = rect.width > 0 ? canvas.width / rect.width : 1;
        const ry = rect.height > 0 ? canvas.height / rect.height : 1;

        if (this.isSeamDebugEnabled() && Math.abs(rx - ry) > 0.0001) {
            console.log("[SeamDebug] buffer:anisotropy", {
                rx,
                ry,
                delta: rx - ry,
                canvasW: canvas.width,
                canvasH: canvas.height,
                rectW: rect.width,
                rectH: rect.height,
            });
        }

        return { rx, ry, rectW: rect.width, rectH: rect.height };
    }

    private getEffPixelsPerWorldUnitXY(): { effX: number; effY: number; rx: number; ry: number } {
        const { rx, ry } = this.getCanvasBufferRatios();
        const z = this.camera.zoom || 1;
        return { effX: z * rx, effY: z * ry, rx, ry };
    }

    private snapCameraToPixelGrid(reason = "snap"): void {
        const { effX, effY } = this.getEffPixelsPerWorldUnitXY();
        if (!Number.isFinite(effX) || !Number.isFinite(effY) || effX <= 0 || effY <= 0) {
            this.logSeamDebug("snapxy:invalid-eff", {
                reason,
                effX,
                effY,
            });
            return;
        }

        const qx = 1 / effX;
        const qy = 1 / effY;

        const snapX = (v: number) => Math.round(v / qx) * qx;
        const snapY = (v: number) => Math.round(v / qy) * qy;

        this.logSeamDebug("snapxy:entry", {
            reason,
            effX,
            effY,
            qx,
            qy,
            scrollXBefore: this.camera.scrollX,
            scrollYBefore: this.camera.scrollY,
            followOffsetXBefore: this.camera.followOffset.x,
            followOffsetYBefore: this.camera.followOffset.y,
        });

        if (this.cameraMode === CameraMode.Exploration) {
            this.explorerFocusOn.x = snapX(this.explorerFocusOn.x);
            this.explorerFocusOn.y = snapY(this.explorerFocusOn.y);
        }

        this.camera.setFollowOffset(snapX(this.camera.followOffset.x), snapY(this.camera.followOffset.y));

        this.camera.scrollX = snapX(this.camera.scrollX);
        this.camera.scrollY = snapY(this.camera.scrollY);

        // IMPORTANT: update clamps to use effX/effY too (see below)
        this.clampScrollToBoundsOnPixelGridXY(effX, effY);

        this.logSeamDebug("snapxy:exit", {
            reason,
            effX,
            effY,
            scrollXAfter: this.camera.scrollX,
            scrollYAfter: this.camera.scrollY,
            scrollXPx: this.camera.scrollX * effX,
            scrollYPx: this.camera.scrollY * effY,
            followOffsetXAfter: this.camera.followOffset.x,
            followOffsetYAfter: this.camera.followOffset.y,
        });
    }

    private clampScrollToBoundsOnPixelGridXY(effX: number, effY: number): void {
        const bounds = this.getCameraBounds();
        if (!bounds) {
            this.logSeamDebug("clampxy:no-bounds", { effX, effY });
            return;
        }

        const zoom = this.camera.zoom || 1;
        const viewWidth = this.camera.width / zoom;
        const viewHeight = this.camera.height / zoom;

        const minScrollX = bounds.x;
        const minScrollY = bounds.y;
        const maxScrollX = Math.max(minScrollX, bounds.x + bounds.width - viewWidth);
        const maxScrollY = Math.max(minScrollY, bounds.y + bounds.height - viewHeight);

        // Convert bounds to framebuffer pixels
        const minXPx = Math.ceil(minScrollX * effX);
        const maxXPx = Math.floor(maxScrollX * effX);
        const minYPx = Math.ceil(minScrollY * effY);
        const maxYPx = Math.floor(maxScrollY * effY);

        // Your inset logic, but per axis
        const insetMinXPx = maxXPx - minXPx >= this.BOUNDARY_INSET_PX * 2 ? minXPx + this.BOUNDARY_INSET_PX : minXPx;
        const insetMaxXPx = maxXPx - minXPx >= this.BOUNDARY_INSET_PX * 2 ? maxXPx - this.BOUNDARY_INSET_PX : maxXPx;

        const xPx = Clamp(Math.round(this.camera.scrollX * effX), insetMinXPx, insetMaxXPx);
        const yPx = Clamp(Math.round(this.camera.scrollY * effY), minYPx, maxYPx);

        this.logSeamDebug("clampxy:computed", {
            effX,
            effY,
            zoom,
            viewWidth,
            viewHeight,
            minScrollX,
            maxScrollX,
            minScrollY,
            maxScrollY,
            minXPx,
            maxXPx,
            minYPx,
            maxYPx,
            insetMinXPx,
            insetMaxXPx,
            xPx,
            yPx,
            scrollXPxBefore: this.camera.scrollX * effX,
            scrollYPxBefore: this.camera.scrollY * effY,
        });

        this.camera.scrollX = xPx / effX;
        this.camera.scrollY = yPx / effY;

        this.logSeamDebug("clampxy:applied", {
            effX,
            effY,
            scrollX: this.camera.scrollX,
            scrollY: this.camera.scrollY,
            scrollXPx: this.camera.scrollX * effX,
            scrollYPx: this.camera.scrollY * effY,
        });
    }

    private logSeamDebug(stage: string, data: Record<string, unknown> = {}): void {
        if (!this.isSeamDebugEnabled()) {
            return;
        }

        const canvas = this.scene.sys.game.canvas as HTMLCanvasElement | undefined;
        const rect = canvas?.getBoundingClientRect();
        const bounds = this.getCameraBounds();
        const eff = this.getEffPixelsPerWorldUnit();
        const tileSize = 32;
        const rx = canvas && rect && rect.width > 0 ? canvas.width / rect.width : undefined;
        const ry = canvas && rect && rect.height > 0 ? canvas.height / rect.height : undefined;
        const rxNum = typeof rx === "number" ? rx : undefined;
        const ryNum = typeof ry === "number" ? ry : undefined;
        const effX = typeof rxNum === "number" ? (this.camera.zoom || 1) * rxNum : undefined;
        const effY = typeof ryNum === "number" ? (this.camera.zoom || 1) * ryNum : undefined;
        const effXNum = typeof effX === "number" ? effX : undefined;
        const effYNum = typeof effY === "number" ? effY : undefined;
        const worldView = this.camera.worldView;
        const followTarget = (this.camera as unknown as { _follow?: { x: number; y: number } | null })._follow;
        const renderRoundPixels = (
            this.camera as unknown as {
                renderRoundPixels?: boolean;
            }
        ).renderRoundPixels;
        const fitExcludeTopWorld = this.getFitExcludeTopWorld();

        console.log(`[SeamDebug] ${stage}`, {
            mode: this.cameraMode,
            zoom: this.camera.zoom,
            scaleZoom: this.scene.scale.zoom,
            roundPixels: this.camera.roundPixels,
            renderRoundPixels,
            eff,
            scrollX: this.camera.scrollX,
            scrollY: this.camera.scrollY,
            scrollXPx: Number.isFinite(eff) ? this.camera.scrollX * eff : undefined,
            scrollYPx: Number.isFinite(eff) ? this.camera.scrollY * eff : undefined,
            worldViewX: worldView.x,
            worldViewY: worldView.y,
            worldViewW: worldView.width,
            worldViewH: worldView.height,
            boundsX: bounds?.x,
            boundsY: bounds?.y,
            boundsW: bounds?.width,
            boundsH: bounds?.height,
            hasFollowTarget: Boolean(followTarget),
            followTargetX: followTarget?.x,
            followTargetY: followTarget?.y,
            canvasW: canvas?.width,
            canvasH: canvas?.height,
            rectW: rect?.width,
            rectH: rect?.height,
            rx,
            ry,
            ratioDelta: typeof rxNum === "number" && typeof ryNum === "number" ? rxNum - ryNum : undefined,
            effX,
            effY,
            tilePx: Number.isFinite(eff) ? tileSize * eff : undefined,
            tilePxX: typeof effXNum === "number" ? tileSize * effXNum : undefined,
            tilePxY: typeof effYNum === "number" ? tileSize * effYNum : undefined,
            tileFrac: Number.isFinite(eff) ? (tileSize * eff) % 1 : undefined,
            tileFracX: typeof effXNum === "number" ? (tileSize * effXNum) % 1 : undefined,
            tileFracY: typeof effYNum === "number" ? (tileSize * effYNum) % 1 : undefined,
            fitExcludeTopWorld,
            dpr: window.devicePixelRatio ?? 1,
            ...data,
        });
    }

    private isSeamDebugEnabled(): boolean {
        const seamDebugWindow = window as unknown as {
            __waSeam?: {
                debugLogs?: boolean;
            };
        };
        return seamDebugWindow.__waSeam?.debugLogs ?? this.CAMERA_SEAM_DEBUG_DEFAULT;
    }

    private resistZoomCallback: ((time: number, delta: number) => void) | undefined;
    private resistZoom(time: number, delta: number): void {
        if (this.animationInProgress) {
            return;
        }
        // If we are out of resistance zone, let's stop the resistance.
        if (
            !this.isBetween(
                this.waScaleManager.zoomModifier,
                this._resistanceStartZoomLevel,
                this._resistanceEndZoomLevel
            )
        ) {
            this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.resistZoomCallback);
            this.resistZoomCallback = undefined;
            this.scene.removeWhiteMask();
            if (
                this.resistanceCallback &&
                ((this._resistanceStartZoomLevel < this._resistanceEndZoomLevel &&
                    this.waScaleManager.zoomModifier > this._resistanceEndZoomLevel) ||
                    (this._resistanceEndZoomLevel < this._resistanceStartZoomLevel &&
                        this.waScaleManager.zoomModifier < this._resistanceEndZoomLevel))
            ) {
                this.resistanceCallback();
                this.wallDownDate = 0;
                this.resistanceZoneEnterDate = 0;
                debugZoom("We passed through resistance zone. Resistance wall is back up");
                debugZoom("this._resistanceStartZoomLevel", this._resistanceStartZoomLevel);
                debugZoom("this._resistanceEndZoomLevel", this._resistanceEndZoomLevel);
                debugZoom("this.waScaleManager.zoomModifier", this.waScaleManager.zoomModifier);
            } else {
                this.wallDownDate = Date.now();
                this.resistanceZoneEnterDate = 0;
                debugZoom("Resistance wall is broken because we left the resistance zone");
                debugZoom("this._resistanceStartZoomLevel", this._resistanceStartZoomLevel);
                debugZoom("this._resistanceEndZoomLevel", this._resistanceEndZoomLevel);
                debugZoom("this.waScaleManager.zoomModifier", this.waScaleManager.zoomModifier);
            }

            return;
        }

        // Let's calculate the new zoom level
        // Our target point is 10% before the resistance zone
        const targetZoom =
            this._resistanceStartZoomLevel - (this._resistanceEndZoomLevel - this._resistanceStartZoomLevel) * 0.1;

        const newZoom =
            (this.targetZoomModifier ?? this.waScaleManager.zoomModifier) +
            (((targetZoom - (this.targetZoomModifier ?? this.waScaleManager.zoomModifier)) * delta) / 250) *
                this._resistanceStrength;
        //this.targetZoomModifier = newZoom;

        this.animateToZoomLevel(newZoom);

        // If the wall is not broken and we spent more than 2 seconds in the resistance zone, let's break the wall.
        if (this.wallDownDate === 0 && Date.now() - this.resistanceZoneEnterDate > 2000) {
            this.wallDownDate = Date.now();
            debugZoom("Resistance wall is broken because we spent 2 seconds in the resistance zone");
        }

        // The alpha is calculated based on the distance between the current zoom level and the resistance zone
        // The closer we are to the resistance zone, the more the alpha is important.
        // We apply a "sqrt" function to make the white layer appear gently if we are close to the start of the resistance
        // zone and faster as we are closer to the end of the resistance zone.
        const alpha = Clamp(
            1 -
                Math.sqrt(
                    (this.waScaleManager.zoomModifier - this._resistanceEndZoomLevel) /
                        (this._resistanceStartZoomLevel - this._resistanceEndZoomLevel)
                ),
            0,
            1
        );

        this.scene.applyWhiteMask(alpha);
    }

    /**
     * Set the resistance zone for the zoom level. The resistance zone is a zone where the camera will resist to zoom in or out.
     * If the resistance zone is overcome, the callback will be called.
     *
     * There can be only one resistance zone at a time.
     *
     * @param startZoomLevel
     * @param endZoomLevel
     * @param strength
     * @param callback
     * @param enableResistanceWall
     * @param resistanceRadiusAroundWoka If set, the resistance is enabled ONLY if the camera is within this radius around the woka
     * @param player
     */
    public setResistanceZone(
        startZoomLevel: number,
        endZoomLevel: number,
        strength: number,
        callback: () => void,
        enableResistanceWall: boolean,
        resistanceRadiusAroundWoka: number | undefined,
        player: Player
    ): void {
        this._resistanceStartZoomLevel = startZoomLevel;
        this._resistanceEndZoomLevel = endZoomLevel;
        this._resistanceStrength = strength;
        this.enableResistanceWall = enableResistanceWall;
        this.resistanceRadiusAroundWoka = resistanceRadiusAroundWoka;
        this.player = player;

        this.resistanceCallback = callback;
    }

    public disableResistanceZone(): void {
        this.scene.removeWhiteMask();
        if (this.resistZoomCallback) {
            this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.resistZoomCallback);
            this.resistZoomCallback = undefined;
        }
    }

    get resistanceStartZoomLevel(): number {
        return this._resistanceStartZoomLevel;
    }

    get resistanceEndZoomLevel(): number {
        return this._resistanceEndZoomLevel;
    }

    emit(event: string | symbol, ...args: unknown[]): boolean {
        // If the camera is defined on Exploration mode, the camera manager events will be not emitted
        if (event === CameraManagerEvent.CameraUpdate && CameraMode.Exploration === this.cameraMode) return false;
        return super.emit(event, ...args);
    }

    setSpeed(speed: { x: number; y: number }) {
        this.cameraSpeed = speed;
        this.explorerFocusOnTarget = undefined;
        this.startAnimation();
    }

    stopSpeed() {
        this.cameraSpeed = undefined;
    }

    public cancelSmoothZoomAndAnchor(): void {
        this.targetZoomModifier = undefined;
        this.targetDirection = undefined;
        this.clearZoomAnchor();
    }

    scrollCamera(x: number, y: number): void {
        this.logSeamDebug("scroll:request", {
            deltaX: x,
            deltaY: y,
            explorerXBefore: this.explorerFocusOn.x,
            explorerYBefore: this.explorerFocusOn.y,
        });
        this.explorerFocusOn.x += x;
        this.explorerFocusOn.y += y;
        this.clampExplorerFocus();

        this.explorerFocusOnTarget = undefined;
        this.logSeamDebug("scroll:applied", {
            explorerXAfter: this.explorerFocusOn.x,
            explorerYAfter: this.explorerFocusOn.y,
        });

        this.emit(CameraManagerEvent.CameraUpdate, this.getCameraUpdateEventData());
        this.scene.markDirty();
    }

    adjustCameraAnchor(deltaX: number, deltaY: number): void {
        this.logSeamDebug("anchor:adjust:request", {
            deltaX,
            deltaY,
            mode: this.cameraMode,
        });
        if (this.cameraMode === CameraMode.Exploration) {
            this.scrollCamera(deltaX, deltaY);
            return;
        }

        const hasFollowTarget = Boolean(
            (this.camera as unknown as { followTarget?: Phaser.GameObjects.GameObject }).followTarget
        );
        if (hasFollowTarget) {
            this.camera.setFollowOffset(this.camera.followOffset.x + deltaX, this.camera.followOffset.y + deltaY);
            this.logSeamDebug("anchor:adjust:follow-offset", {
                followOffsetX: this.camera.followOffset.x,
                followOffsetY: this.camera.followOffset.y,
            });
            this.scene.markDirty();
            return;
        }

        this.camera.scrollX += deltaX;
        this.camera.scrollY += deltaY;
        this.logSeamDebug("anchor:adjust:scroll", {
            scrollX: this.camera.scrollX,
            scrollY: this.camera.scrollY,
        });
        this.scene.markDirty();
    }

    setZoomAnchor(anchor: { screenX: number; screenY: number; worldX: number; worldY: number } | undefined): void {
        this.zoomAnchor = anchor;
        this.logSeamDebug("anchor:set", {
            hasAnchor: Boolean(anchor),
            screenX: anchor?.screenX,
            screenY: anchor?.screenY,
            worldX: anchor?.worldX,
            worldY: anchor?.worldY,
        });
    }

    clearZoomAnchor(): void {
        this.zoomAnchor = undefined;
        this.logSeamDebug("anchor:clear");
    }

    private readMapFitExcludeTopWorldFromMap(): number {
        const rawValue = PropertyUtils.findProperty(
            this.CAMERA_FIT_EXCLUDE_TOP_PROPERTY,
            this.scene.mapFile?.properties
        );
        if (rawValue === undefined) {
            return 0;
        }

        if (typeof rawValue !== "number" || !Number.isFinite(rawValue)) {
            console.warn(
                `[Camera] Ignoring map property "${this.CAMERA_FIT_EXCLUDE_TOP_PROPERTY}" because it is not a finite number.`,
                rawValue
            );
            return 0;
        }

        return Math.max(0, rawValue);
    }

    private ensureCameraExperimentControls(): void {
        const cameraDebugWindow = window as unknown as {
            __waCamera?: Partial<WaCameraExperimentControls>;
        };

        if (!cameraDebugWindow.__waCamera) {
            cameraDebugWindow.__waCamera = {
                topOverscanEnabled: false,
                topOverscanWorld: this.CAMERA_TOP_OVERSCAN_DEFAULT_WORLD,
            };
            return;
        }

        if (typeof cameraDebugWindow.__waCamera.topOverscanEnabled !== "boolean") {
            cameraDebugWindow.__waCamera.topOverscanEnabled = false;
        }

        if (
            typeof cameraDebugWindow.__waCamera.topOverscanWorld !== "number" ||
            !Number.isFinite(cameraDebugWindow.__waCamera.topOverscanWorld)
        ) {
            cameraDebugWindow.__waCamera.topOverscanWorld = this.CAMERA_TOP_OVERSCAN_DEFAULT_WORLD;
        }
    }

    private getCameraExperimentControls(): WaCameraExperimentControls {
        this.ensureCameraExperimentControls();
        const cameraDebugWindow = window as unknown as {
            __waCamera?: Partial<WaCameraExperimentControls>;
        };
        return {
            topOverscanEnabled: cameraDebugWindow.__waCamera?.topOverscanEnabled === true,
            topOverscanWorld: cameraDebugWindow.__waCamera?.topOverscanWorld ?? this.CAMERA_TOP_OVERSCAN_DEFAULT_WORLD,
        };
    }

    private getFitExcludeTopWorld(): number {
        const maxExcludeWorld = Math.max(0, this.mapSize.height - 1);
        return Clamp(this.mapFitExcludeTopWorldFromMap, 0, maxExcludeWorld);
    }

    private getTopBoundaryOverscanWorld(): number {
        const controls = this.getCameraExperimentControls();
        if (!controls.topOverscanEnabled) {
            return 0;
        }
        const maxOverscanWorld = Math.max(0, this.mapSize.height);
        return Clamp(controls.topOverscanWorld, 0, maxOverscanWorld);
    }

    private getMapBoundsWithSlack(): { x: number; y: number; width: number; height: number; topOverscanWorld: number } {
        const slack = this.CAMERA_BOUNDARY_SLACK_WORLD;
        const topOverscanWorld = this.getTopBoundaryOverscanWorld();

        return {
            x: -slack,
            y: -slack - topOverscanWorld,
            width: this.mapSize.width + slack * 2,
            height: this.mapSize.height + slack * 2 + topOverscanWorld,
            topOverscanWorld,
        };
    }

    private buildMapBoundsSignature(): string {
        const bounds = this.getMapBoundsWithSlack();
        return `${bounds.x}:${bounds.y}:${bounds.width}:${bounds.height}`;
    }

    private buildFitZoomSignature(): string {
        return `${this.getFitExcludeTopWorld()}:${this.cameraMode}`;
    }

    private syncFitZoomConstraints(): void {
        const signature = this.buildFitZoomSignature();
        if (signature === this.lastAppliedFitZoomSignature) {
            return;
        }

        this.lastAppliedFitZoomSignature = signature;
        this.refreshZoomBounds();
        const minCameraZoom = this.getMinimumCameraZoomForCurrentView();
        if (this.camera.zoom + 0.000001 < minCameraZoom) {
            this.waScaleManager.setRuntimeCameraZoom(minCameraZoom, this.camera);
            if (this.cameraMode === CameraMode.Exploration) {
                this.clampExplorerFocus();
            }
            this.emit(CameraManagerEvent.CameraUpdate, this.getCameraUpdateEventData());
        }
        this.scene.markDirty();
        this.logSeamDebug("fit:constraints-sync", {
            signature,
            fitExcludeTopWorld: this.getFitExcludeTopWorld(),
            minCameraZoom,
        });
    }

    private syncMapBoundsWithDevToolsOverrides(): void {
        if (this.mapEditorModeOpened) {
            return;
        }

        if (this.cameraMode === CameraMode.Exploration && this.explorationAllowOutsideMap) {
            return;
        }

        const currentSignature = this.buildMapBoundsSignature();
        if (currentSignature === this.lastAppliedMapBoundsSignature) {
            return;
        }

        this.applyMapBoundsWithSlack();
        if (this.cameraMode === CameraMode.Exploration) {
            this.clampExplorerFocus();
        }
        this.scene.markDirty();
        this.logSeamDebug("bounds:devtools-sync", {
            signature: currentSignature,
        });
    }

    private clampExplorerFocus(): void {
        const beforeX = this.explorerFocusOn.x;
        const beforeY = this.explorerFocusOn.y;
        if (this.explorationAllowOutsideMap) {
            this.explorerFocusOn.x = Clamp(this.explorerFocusOn.x, 0, this.mapSize.width);
            this.explorerFocusOn.y = Clamp(this.explorerFocusOn.y, 0, this.mapSize.height);
            this.logSeamDebug("exploration:clamp", {
                allowOutsideMap: true,
                beforeX,
                beforeY,
                afterX: this.explorerFocusOn.x,
                afterY: this.explorerFocusOn.y,
                changed: beforeX !== this.explorerFocusOn.x || beforeY !== this.explorerFocusOn.y,
            });

            return;
        }

        const halfViewWidth = this.camera.worldView.width / 2;
        const halfViewHeight = this.camera.worldView.height / 2;
        const bounds = this.getMapBoundsWithSlack();
        const minX = bounds.x + halfViewWidth;
        const maxX = bounds.x + bounds.width - halfViewWidth;
        const minY = bounds.y + halfViewHeight;
        const maxY = bounds.y + bounds.height - halfViewHeight;

        if (minX > maxX) {
            this.explorerFocusOn.x = this.mapSize.width / 2;
        } else {
            this.explorerFocusOn.x = Clamp(this.explorerFocusOn.x, minX, maxX);
        }

        if (minY > maxY) {
            this.explorerFocusOn.y = this.mapSize.height / 2;
        } else {
            this.explorerFocusOn.y = Clamp(this.explorerFocusOn.y, minY, maxY);
        }

        this.logSeamDebug("exploration:clamp", {
            allowOutsideMap: false,
            beforeX,
            beforeY,
            afterX: this.explorerFocusOn.x,
            afterY: this.explorerFocusOn.y,
            minX,
            maxX,
            minY,
            maxY,
            topOverscanWorld: bounds.topOverscanWorld,
            changed: beforeX !== this.explorerFocusOn.x || beforeY !== this.explorerFocusOn.y,
        });
    }

    private applyMapBoundsWithSlack(): void {
        const bounds = this.getMapBoundsWithSlack();
        this.camera.setBounds(bounds.x, bounds.y, bounds.width, bounds.height, false);
        this.lastAppliedMapBoundsSignature = this.buildMapBoundsSignature();
        this.logSeamDebug("bounds:applied", bounds);
    }

    private applyZoomAnchor(): void {
        if (!this.zoomAnchor) {
            return;
        }
        const { screenX, screenY, worldX, worldY } = this.zoomAnchor;
        this.logSeamDebug("anchor:apply:start", {
            screenX,
            screenY,
            worldX,
            worldY,
        });

        if (this.cameraMode === CameraMode.Exploration) {
            // In exploration mode, the camera follows explorerFocusOn.
            // Compute the center directly from anchor + zoom to avoid one-frame lag from getWorldPoint().
            const zoom = this.camera.zoom || 1;
            const desiredCenterX = worldX - (screenX - this.camera.width / 2) / zoom;
            const desiredCenterY = worldY - (screenY - this.camera.height / 2) / zoom;
            const deltaX = desiredCenterX - this.explorerFocusOn.x;
            const deltaY = desiredCenterY - this.explorerFocusOn.y;
            this.logSeamDebug("anchor:apply:exploration", {
                zoom,
                desiredCenterX,
                desiredCenterY,
                deltaX,
                deltaY,
                explorerX: this.explorerFocusOn.x,
                explorerY: this.explorerFocusOn.y,
            });

            if (Math.abs(deltaX) > 0.0001 || Math.abs(deltaY) > 0.0001) {
                this.scrollCamera(deltaX, deltaY);
            }

            return;
        }

        const current = this.camera.getWorldPoint(screenX, screenY);
        const deltaX = worldX - current.x;
        const deltaY = worldY - current.y;
        this.logSeamDebug("anchor:apply:follow-focus", {
            currentX: current.x,
            currentY: current.y,
            deltaX,
            deltaY,
        });
        if (Math.abs(deltaX) > 0.0001 || Math.abs(deltaY) > 0.0001) {
            this.adjustCameraAnchor(deltaX, deltaY);
        }
    }

    private getDesiredRoundPixels(): boolean {
        if (this.cameraMode === CameraMode.Exploration) {
            return false;
        }
        const zoom = this.camera.zoom || 1;
        return Math.abs(zoom - Math.round(zoom)) <= this.ZOOM_INTEGER_EPSILON;
    }

    private syncRoundPixelsForModeAndZoom(reason: string): void {
        const desiredRoundPixels = this.getDesiredRoundPixels();
        if (this.camera.roundPixels === desiredRoundPixels) {
            return;
        }
        this.camera.roundPixels = desiredRoundPixels;
        this.logSeamDebug("roundPixels:toggle", {
            reason,
            desiredRoundPixels,
        });
    }

    private updateRoundPixelsForMode(): void {
        this.syncRoundPixelsForModeAndZoom("mode_change");
    }
}
